import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box, Button, Typography, Card, CardContent,
    Table, Select, Option, FormControl, FormLabel,
    CircularProgress, Alert, Snackbar, Divider, Chip,
    IconButton, Modal, ModalDialog, ModalClose
} from '@mui/joy';
import axios from 'axios';
import {
    Download, VerifiedUser, Refresh, Event, Person, CalendarToday,
    ChevronLeft, ChevronRight, FirstPage, LastPage, NavigateBefore, NavigateNext, Close, Search, Edit, Email
} from '@mui/icons-material';
import { workshopDomain } from '../../util/domain';

const CertificateAnalytics = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [events, setEvents] = useState([]);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [analytics, setAnalytics] = useState(null);
    const [certificateAnalytics, setCertificateAnalytics] = useState(null);
    const [selectedCertificateId, setSelectedCertificateId] = useState(null);
    const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(50);
    const [loadingDetails, setLoadingDetails] = useState(false);
    const [currentCertificateIndex, setCurrentCertificateIndex] = useState(-1);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [editingStudent, setEditingStudent] = useState(null);
    const [editFormData, setEditFormData] = useState({ name: '', email: '' });
    const [saving, setSaving] = useState(false);
    const [sendingEmail, setSendingEmail] = useState(null);

    // Check authentication and admin role
    useEffect(() => {
        const token = localStorage.getItem('token');
        if (!token) {
            navigate('/admin');
            return;
        }

        const user = JSON.parse(localStorage.getItem('user') || '{}');
        // Check if user has Admin role
        const roles = user.roles || [];
        const isAdmin = roles.includes('Admin');
        if (!isAdmin) {
            navigate('/admin/home');
            return;
        }

        fetchEvents();
        fetchAnalytics();
    }, [navigate]);

    const fetchEvents = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${workshopDomain}/api/events/all`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (response.data.success) {
                setEvents(response.data.events);
            }
        } catch (error) {
            console.error('Error fetching events:', error);
            showNotification('Failed to fetch events', 'error');
        }
    };

    const fetchAnalytics = async (page = currentPage) => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const params = {
                page: page,
                limit: itemsPerPage
            };

            if (selectedEvent) {
                params.eventHashId = selectedEvent.hash_id;
            }
            if (startDate) {
                params.startDate = startDate;
            }
            if (endDate) {
                params.endDate = endDate;
            }
            if (searchQuery && searchQuery.trim()) {
                params.search = searchQuery.trim();
            }

            const response = await axios.get(`${workshopDomain}/api/certificate/analytics`, {
                headers: { Authorization: `Bearer ${token}` },
                params
            });

            if (response.data.success) {
                setAnalytics(response.data);
                setCurrentPage(page);
            }
        } catch (error) {
            console.error('Error fetching analytics:', error);
            showNotification('Failed to fetch analytics', 'error');
        } finally {
            setLoading(false);
        }
    };

    const fetchCertificateAnalytics = async (certificateId, index = null) => {
        try {
            setLoadingDetails(true);
            const token = localStorage.getItem('token');
            const response = await axios.get(`${workshopDomain}/api/certificate/analytics/${certificateId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data.success) {
                setCertificateAnalytics(response.data.analytics);
                setSelectedCertificateId(certificateId);
                if (index !== null) {
                    setCurrentCertificateIndex(index);
                }
            }
        } catch (error) {
            console.error('Error fetching certificate analytics:', error);
            showNotification('Failed to fetch certificate analytics', 'error');
        } finally {
            setLoadingDetails(false);
        }
    };

    const handleViewDetails = (certificateId, index) => {
        setCurrentCertificateIndex(index);
        setShowDetailsModal(true);
        fetchCertificateAnalytics(certificateId, index);
    };

    const handlePrevious = () => {
        if (currentCertificateIndex > 0 && paginatedCertificates.length > 0) {
            const prevIndex = currentCertificateIndex - 1;
            const prevCert = paginatedCertificates[prevIndex];
            setCurrentCertificateIndex(prevIndex);
            fetchCertificateAnalytics(prevCert.certificate_id, prevIndex);
        }
    };

    const handleNext = () => {
        if (currentCertificateIndex < paginatedCertificates.length - 1 && paginatedCertificates.length > 0) {
            const nextIndex = currentCertificateIndex + 1;
            const nextCert = paginatedCertificates[nextIndex];
            setCurrentCertificateIndex(nextIndex);
            fetchCertificateAnalytics(nextCert.certificate_id, nextIndex);
        }
    };

    const handleCloseModal = () => {
        setShowDetailsModal(false);
        setCertificateAnalytics(null);
        setSelectedCertificateId(null);
        setCurrentCertificateIndex(-1);
    };

    const handleEditStudent = (cert) => {
        setEditingStudent(cert);
        setEditFormData({
            name: cert.student_name || '',
            email: cert.student_email || ''
        });
        setShowEditModal(true);
    };

    const handleCloseEditModal = () => {
        setShowEditModal(false);
        setEditingStudent(null);
        setEditFormData({ name: '', email: '' });
    };

    const handleSaveStudent = async () => {
        if (!editingStudent || !editFormData.name.trim() || !editFormData.email.trim()) {
            showNotification('Name and email are required', 'error');
            return;
        }

        // Validate email format
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(editFormData.email.trim())) {
            showNotification('Invalid email format', 'error');
            return;
        }

        try {
            setSaving(true);
            const token = localStorage.getItem('token');
            const response = await axios.put(
                `${workshopDomain}/api/certificate/student/${editingStudent.student_hash_id}`,
                {
                    name: editFormData.name.trim(),
                    email: editFormData.email.trim()
                },
                {
                    headers: { Authorization: `Bearer ${token}` }
                }
            );

            if (response.data.success) {
                showNotification('Student details updated successfully', 'success');
                handleCloseEditModal();
                // Refresh the analytics data
                fetchAnalytics(currentPage);
            }
        } catch (error) {
            console.error('Error updating student:', error);
            showNotification(
                error.response?.data?.message || 'Failed to update student details',
                'error'
            );
        } finally {
            setSaving(false);
        }
    };

    const handleResendEmail = async (certificateId, studentEmail) => {
        if (!studentEmail) {
            showNotification('Student email not found. Please update student email first.', 'error');
            return;
        }

        try {
            setSendingEmail(certificateId);
            const token = localStorage.getItem('token');
            const response = await axios.post(
                `${workshopDomain}/api/certificate/resend-email/${certificateId}`,
                {},
                {
                    headers: { Authorization: `Bearer ${token}` }
                }
            );

            if (response.data.success) {
                showNotification('Certificate email sent successfully', 'success');
            }
        } catch (error) {
            console.error('Error resending certificate email:', error);
            showNotification(
                error.response?.data?.message || 'Failed to send certificate email',
                'error'
            );
        } finally {
            setSendingEmail(null);
        }
    };

    const showNotification = (message, severity = 'success') => {
        setNotification({ open: true, message, severity });
    };

    const closeNotification = () => {
        setNotification({ ...notification, open: false });
    };

    const handleFilterChange = () => {
        setCurrentPage(1); // Reset to first page when filters change
        fetchAnalytics(1);
    };

    useEffect(() => {
        fetchAnalytics(1); // Always start from page 1 when filters change
    }, [selectedEvent, startDate, endDate]);

    // Get pagination info from server response
    const pagination = analytics?.pagination || {
        currentPage: 1,
        totalPages: 1,
        totalItems: 0,
        itemsPerPage: itemsPerPage,
        hasNextPage: false,
        hasPrevPage: false
    };

    const handlePageChange = (newPage) => {
        fetchAnalytics(newPage);
        // Scroll to top when page changes
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    // Use certificates directly from server response (already paginated)
    const paginatedCertificates = analytics?.certificates || [];

    return (
        <Box sx={{ p: 3 }}>
            <Typography level="h2" sx={{ mb: 3 }}>
                Certificate Analytics
            </Typography>

            {/* Filters */}
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Typography level="h3" sx={{ mb: 2 }}>Filters</Typography>
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        <FormControl sx={{ minWidth: 200 }}>
                            <FormLabel>Event</FormLabel>
                            <Select
                                value={selectedEvent?.hash_id || null}
                                onChange={(_, value) => {
                                    const event = events.find(e => e.hash_id === value);
                                    setSelectedEvent(event || null);
                                }}
                                placeholder="All Events"
                            >
                                <Option value={null}>All Events</Option>
                                {events.map((event) => (
                                    <Option key={event.hash_id} value={event.hash_id}>
                                        {event.title}
                                    </Option>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControl sx={{ minWidth: 200 }}>
                            <FormLabel>Start Date</FormLabel>
                            <input
                                type="date"
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                                style={{
                                    padding: '8px 12px',
                                    border: '1px solid #ccc',
                                    borderRadius: '4px',
                                    fontSize: '14px'
                                }}
                            />
                        </FormControl>

                        <FormControl sx={{ minWidth: 200 }}>
                            <FormLabel>End Date</FormLabel>
                            <input
                                type="date"
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                                style={{
                                    padding: '8px 12px',
                                    border: '1px solid #ccc',
                                    borderRadius: '4px',
                                    fontSize: '14px'
                                }}
                            />
                        </FormControl>

                        <FormControl sx={{ minWidth: 250 }}>
                            <FormLabel>Search Student (Name/Email/Mobile)</FormLabel>
                            <Box sx={{ position: 'relative' }}>
                                <Search sx={{ 
                                    position: 'absolute', 
                                    left: '8px', 
                                    top: '50%', 
                                    transform: 'translateY(-50%)',
                                    color: 'text.secondary',
                                    fontSize: '20px'
                                }} />
                                <input
                                    type="text"
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    placeholder="Search by name, email, or mobile"
                                    style={{
                                        padding: '8px 12px 8px 36px',
                                        border: '1px solid #ccc',
                                        borderRadius: '4px',
                                        fontSize: '14px',
                                        width: '100%'
                                    }}
                                />
                            </Box>
                        </FormControl>

                        <Button
                            startDecorator={<Refresh />}
                            onClick={handleFilterChange}
                            sx={{ mt: 3 }}
                        >
                            Refresh
                        </Button>
                    </Box>
                </CardContent>
            </Card>

            {/* Summary Statistics */}
            {analytics && (
                <Card sx={{ mb: 3 }}>
                    <CardContent>
                        <Typography level="h3" sx={{ mb: 2 }}>Summary Statistics</Typography>
                        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                            <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'background.level1', borderRadius: '8px', minWidth: 150 }}>
                                <Download sx={{ fontSize: 40, color: 'primary.500', mb: 1 }} />
                                <Typography level="h4">{parseInt(analytics.summary.total_downloads || 0)}</Typography>
                                <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                                    Total Downloads
                                </Typography>
                            </Box>
                            <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'background.level1', borderRadius: '8px', minWidth: 150 }}>
                                <VerifiedUser sx={{ fontSize: 40, color: 'success.500', mb: 1 }} />
                                <Typography level="h4">{parseInt(analytics.summary.total_verifications || 0)}</Typography>
                                <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                                    Total Verifications
                                </Typography>
                            </Box>
                            <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'background.level1', borderRadius: '8px', minWidth: 150 }}>
                                <Person sx={{ fontSize: 40, color: 'warning.500', mb: 1 }} />
                                <Typography level="h4">{parseInt(analytics.summary.unique_downloads || 0)}</Typography>
                                <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                                    Unique Downloads
                                </Typography>
                            </Box>
                            <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'background.level1', borderRadius: '8px', minWidth: 150 }}>
                                <VerifiedUser sx={{ fontSize: 40, color: 'info.500', mb: 1 }} />
                                <Typography level="h4">{parseInt(analytics.summary.unique_verifications || 0)}</Typography>
                                <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                                    Unique Verifications
                                </Typography>
                            </Box>
                        </Box>
                    </CardContent>
                </Card>
            )}

            {/* Certificates Table */}
            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <CircularProgress />
                </Box>
            ) : analytics && analytics.certificates && analytics.certificates.length > 0 ? (
                <Card>
                    <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography level="h3">Certificate Activity</Typography>
                            <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                                Showing {paginatedCertificates.length > 0 ? ((pagination.currentPage - 1) * pagination.itemsPerPage + 1) : 0}-{Math.min(pagination.currentPage * pagination.itemsPerPage, pagination.totalItems)} of {pagination.totalItems} certificates
                            </Typography>
                        </Box>

                        {/* Search and Pagination Controls */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, gap: 2 }}>
                            {/* Search Field - Left */}
                            <Box sx={{ display: 'flex', gap: 1, flex: 1, maxWidth: 500, alignItems: 'center' }}>
                                <Box sx={{ position: 'relative', flex: 1 }}>
                                    <Search sx={{ 
                                        position: 'absolute', 
                                        left: '8px', 
                                        top: '50%', 
                                        transform: 'translateY(-50%)',
                                        color: 'text.secondary',
                                        fontSize: '20px',
                                        zIndex: 1
                                    }} />
                                    <input
                                        type="text"
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        onKeyPress={(e) => {
                                            if (e.key === 'Enter') {
                                                handleFilterChange();
                                            }
                                        }}
                                        placeholder="Search by name, email, or mobile"
                                        style={{
                                            padding: '8px 12px 8px 36px',
                                            border: '1px solid #ccc',
                                            borderRadius: '4px',
                                            fontSize: '14px',
                                            width: '100%'
                                        }}
                                    />
                                </Box>
                                <Button
                                    variant="solid"
                                    color="primary"
                                    startDecorator={<Search />}
                                    onClick={handleFilterChange}
                                    disabled={loading}
                                >
                                    Search
                                </Button>
                            </Box>

                            {/* Pagination Controls - Right */}
                            {pagination.totalPages > 1 && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <IconButton
                                        variant="outlined"
                                        size="sm"
                                        onClick={() => handlePageChange(1)}
                                        disabled={!pagination.hasPrevPage || loading}
                                    >
                                        <FirstPage />
                                    </IconButton>
                                    <IconButton
                                        variant="outlined"
                                        size="sm"
                                        onClick={() => handlePageChange(pagination.currentPage - 1)}
                                        disabled={!pagination.hasPrevPage || loading}
                                    >
                                        <ChevronLeft />
                                    </IconButton>
                                    <Typography level="body-sm" sx={{ mx: 2 }}>
                                        Page {pagination.currentPage} of {pagination.totalPages}
                                    </Typography>
                                    <IconButton
                                        variant="outlined"
                                        size="sm"
                                        onClick={() => handlePageChange(pagination.currentPage + 1)}
                                        disabled={!pagination.hasNextPage || loading}
                                    >
                                        <ChevronRight />
                                    </IconButton>
                                    <IconButton
                                        variant="outlined"
                                        size="sm"
                                        onClick={() => handlePageChange(pagination.totalPages)}
                                        disabled={!pagination.hasNextPage || loading}
                                    >
                                        <LastPage />
                                    </IconButton>
                                </Box>
                            )}
                        </Box>

                        <Table stickyHeader>
                            <thead>
                                <tr>
                                    <th>Student Name</th>
                                    <th>Event</th>
                                    <th>Downloads</th>
                                    <th>Verifications</th>
                                    <th>Last Activity</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {paginatedCertificates.map((cert) => (
                                    <tr key={cert.certificate_id}>
                                        <td>{cert.student_name}</td>
                                        <td>{cert.event_title}</td>
                                        <td>
                                            <Chip color="primary" variant="soft">
                                                {parseInt(cert.download_count || 0)}
                                            </Chip>
                                        </td>
                                        <td>
                                            <Chip color="success" variant="soft">
                                                {parseInt(cert.verification_count || 0)}
                                            </Chip>
                                        </td>
                                        <td>
                                            {cert.last_activity
                                                ? new Date(cert.last_activity).toLocaleString()
                                                : 'Never'}
                                        </td>
                                        <td>
                                            <Box sx={{ display: 'flex', gap: 1 }}>
                                                <Button
                                                    size="sm"
                                                    variant="outlined"
                                                    onClick={() => handleViewDetails(cert.certificate_id, paginatedCertificates.indexOf(cert))}
                                                    disabled={loadingDetails}
                                                >
                                                    {loadingDetails && selectedCertificateId === cert.certificate_id ? 'Loading...' : 'View Details'}
                                                </Button>
                                                <IconButton
                                                    size="sm"
                                                    variant="outlined"
                                                    color="primary"
                                                    onClick={() => handleEditStudent(cert)}
                                                    title="Edit Student Details"
                                                >
                                                    <Edit fontSize="small" />
                                                </IconButton>
                                                <IconButton
                                                    size="sm"
                                                    variant="outlined"
                                                    color="success"
                                                    onClick={() => handleResendEmail(cert.certificate_id, cert.student_email)}
                                                    disabled={sendingEmail === cert.certificate_id || !cert.student_email}
                                                    title="Resend Certificate Email"
                                                >
                                                    {sendingEmail === cert.certificate_id ? (
                                                        <CircularProgress size="small" />
                                                    ) : (
                                                        <Email fontSize="small" />
                                                    )}
                                                </IconButton>
                                            </Box>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>

                        {/* Pagination Controls */}
                        {pagination.totalPages > 1 && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1, mt: 3 }}>
                                <IconButton
                                    variant="outlined"
                                    size="sm"
                                    onClick={() => handlePageChange(1)}
                                    disabled={!pagination.hasPrevPage || loading}
                                >
                                    <FirstPage />
                                </IconButton>
                                <IconButton
                                    variant="outlined"
                                    size="sm"
                                    onClick={() => handlePageChange(pagination.currentPage - 1)}
                                    disabled={!pagination.hasPrevPage || loading}
                                >
                                    <ChevronLeft />
                                </IconButton>
                                <Typography level="body-sm" sx={{ mx: 2 }}>
                                    Page {pagination.currentPage} of {pagination.totalPages}
                                </Typography>
                                <IconButton
                                    variant="outlined"
                                    size="sm"
                                    onClick={() => handlePageChange(pagination.currentPage + 1)}
                                    disabled={!pagination.hasNextPage || loading}
                                >
                                    <ChevronRight />
                                </IconButton>
                                <IconButton
                                    variant="outlined"
                                    size="sm"
                                    onClick={() => handlePageChange(pagination.totalPages)}
                                    disabled={!pagination.hasNextPage || loading}
                                >
                                    <LastPage />
                                </IconButton>
                            </Box>
                        )}
                    </CardContent>
                </Card>
            ) : analytics && analytics.certificates && analytics.certificates.length === 0 ? (
                <Alert color="neutral">No certificate analytics data found for the selected filters.</Alert>
            ) : null}

            {/* Certificate Details Modal */}
            <Modal open={showDetailsModal} onClose={handleCloseModal}>
                <ModalDialog
                    aria-labelledby="certificate-details-modal"
                    sx={{
                        maxWidth: 900,
                        width: '100%',
                        maxHeight: '90vh',
                        overflow: 'auto',
                        '& .MuiModalDialog-closeButton': { display: 'none' }
                    }}
                >
                    {loadingDetails ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4 }}>
                            <CircularProgress />
                            <Typography level="body-md" sx={{ ml: 2 }}>Loading certificate details...</Typography>
                        </Box>
                    ) : certificateAnalytics && selectedCertificateId ? (
                        <>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, pr: 4 }}>
                                <Typography level="h2" id="certificate-details-modal">
                                    Certificate Details
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                    <IconButton
                                        variant="outlined"
                                        size="sm"
                                        onClick={handlePrevious}
                                        disabled={currentCertificateIndex <= 0 || loadingDetails}
                                    >
                                        <NavigateBefore />
                                    </IconButton>
                                    <Typography level="body-sm" sx={{ mx: 1 }}>
                                        {currentCertificateIndex + 1} of {paginatedCertificates.length}
                                    </Typography>
                                    <IconButton
                                        variant="outlined"
                                        size="sm"
                                        onClick={handleNext}
                                        disabled={currentCertificateIndex >= paginatedCertificates.length - 1 || loadingDetails}
                                    >
                                        <NavigateNext />
                                    </IconButton>
                                    <IconButton
                                        variant="plain"
                                        size="sm"
                                        onClick={handleCloseModal}
                                        sx={{ ml: 1 }}
                                        aria-label="Close"
                                    >
                                        <Close />
                                    </IconButton>
                                </Box>
                            </Box>
                            <Divider sx={{ mb: 2 }} />
                            <Box sx={{ mb: 2 }}>
                                <Typography level="body-sm" sx={{ color: 'text.secondary', mb: 1 }}>
                                    Certificate ID: <strong>{selectedCertificateId}</strong>
                                </Typography>
                                {paginatedCertificates[currentCertificateIndex] && (
                                    <>
                                        <Typography level="body-sm" sx={{ color: 'text.secondary', mb: 1 }}>
                                            Student: <strong>{paginatedCertificates[currentCertificateIndex].student_name}</strong>
                                        </Typography>
                                        <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                                            Event: <strong>{paginatedCertificates[currentCertificateIndex].event_title}</strong>
                                        </Typography>
                                    </>
                                )}
                            </Box>
                            <Box sx={{ display: 'flex', gap: 3, mb: 3 }}>
                                <Box>
                                    <Typography level="body-sm" sx={{ color: 'text.secondary' }}>Downloads</Typography>
                                    <Typography level="h4">{parseInt(certificateAnalytics.downloads || 0)}</Typography>
                                </Box>
                                <Box>
                                    <Typography level="body-sm" sx={{ color: 'text.secondary' }}>Verifications</Typography>
                                    <Typography level="h4">{parseInt(certificateAnalytics.verifications || 0)}</Typography>
                                </Box>
                            </Box>

                            {certificateAnalytics.recentActivity && certificateAnalytics.recentActivity.length > 0 && (
                                <Box sx={{ mt: 3 }}>
                                    <Typography level="h4" sx={{ mb: 2 }}>Recent Activity</Typography>
                                    <Table>
                                        <thead>
                                            <tr>
                                                <th>Type</th>
                                                <th>IP Address</th>
                                                <th>Date</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {certificateAnalytics.recentActivity.map((activity, idx) => (
                                                <tr key={idx}>
                                                    <td>
                                                        <Chip
                                                            color={activity.action_type === 'download' ? 'primary' : 'success'}
                                                            variant="soft"
                                                        >
                                                            {activity.action_type}
                                                        </Chip>
                                                    </td>
                                                    <td>{activity.ip_address || 'N/A'}</td>
                                                    <td>{new Date(activity.created_at).toLocaleString()}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </Box>
                            )}

                            {certificateAnalytics.activityByDate && certificateAnalytics.activityByDate.length > 0 && (
                                <Box sx={{ mt: 3 }}>
                                    <Typography level="h4" sx={{ mb: 2 }}>Activity by Date (Last 30 Days)</Typography>
                                    <Table>
                                        <thead>
                                            <tr>
                                                <th>Date</th>
                                                <th>Type</th>
                                                <th>Count</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {certificateAnalytics.activityByDate.map((activity, idx) => (
                                                <tr key={idx}>
                                                    <td>{new Date(activity.date).toLocaleDateString()}</td>
                                                    <td>
                                                        <Chip
                                                            color={activity.action_type === 'download' ? 'primary' : 'success'}
                                                            variant="soft"
                                                        >
                                                            {activity.action_type}
                                                        </Chip>
                                                    </td>
                                                    <td>{parseInt(activity.count || 0)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </Box>
                            )}
                        </>
                    ) : null}
                </ModalDialog>
            </Modal>

            {/* Edit Student Modal */}
            <Modal open={showEditModal} onClose={handleCloseEditModal}>
                <ModalDialog
                    aria-labelledby="edit-student-modal"
                    sx={{
                        maxWidth: 500,
                        width: '100%'
                    }}
                >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography level="h3" id="edit-student-modal">
                            Edit Student Details
                        </Typography>
                        <IconButton
                            variant="plain"
                            size="sm"
                            onClick={handleCloseEditModal}
                            aria-label="Close"
                        >
                            <Close />
                        </IconButton>
                    </Box>
                    <Divider sx={{ mb: 2 }} />

                    {editingStudent && (
                        <>
                            <FormControl sx={{ mb: 2 }}>
                                <FormLabel>Student Name</FormLabel>
                                <input
                                    type="text"
                                    value={editFormData.name}
                                    onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                                    style={{
                                        padding: '8px 12px',
                                        border: '1px solid #ccc',
                                        borderRadius: '4px',
                                        fontSize: '14px',
                                        width: '100%'
                                    }}
                                    placeholder="Enter student name"
                                />
                            </FormControl>

                            <FormControl sx={{ mb: 3 }}>
                                <FormLabel>Email</FormLabel>
                                <input
                                    type="email"
                                    value={editFormData.email}
                                    onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })}
                                    style={{
                                        padding: '8px 12px',
                                        border: '1px solid #ccc',
                                        borderRadius: '4px',
                                        fontSize: '14px',
                                        width: '100%'
                                    }}
                                    placeholder="Enter email address"
                                />
                            </FormControl>

                            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                                <Button
                                    variant="outlined"
                                    onClick={handleCloseEditModal}
                                    disabled={saving}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    variant="solid"
                                    color="primary"
                                    onClick={handleSaveStudent}
                                    disabled={saving || !editFormData.name.trim() || !editFormData.email.trim()}
                                    startDecorator={saving ? <CircularProgress size="sm" /> : null}
                                >
                                    {saving ? 'Saving...' : 'Save Changes'}
                                </Button>
                            </Box>
                        </>
                    )}
                </ModalDialog>
            </Modal>

            {/* Notification Snackbar */}
            <Snackbar
                open={notification.open}
                autoHideDuration={6000}
                onClose={closeNotification}
                color={notification.severity}
            >
                {notification.message}
            </Snackbar>
        </Box>
    );
};

export default CertificateAnalytics;

